import { useGetCampaignsApiV1Suspense } from "@/api/__generated__/api.schema";
import { CampaignUserRegisterStatus } from "@/api/__generated__/models";
import { PageContainer } from "@/components/common/page-container";
import { CampaignCard } from "@/components/shared/campaign-card";
import { CAMPAIGN_INFO, DEBUT_BOONE_CAMPAIGN_ID } from "@/constants/campaign";
import { routes } from "@/constants/url";
import { useWorkspaceId } from "@/hooks/auth/use-workspace";
import { useWorkspaceNavigate } from "@/lib/router/use-workspace-navigate";
import { WorkspaceLink } from "@/lib/router/workspace-link";
import { calculateRemainingDays } from "@/utils/date";
import { getSnsLabel } from "@/utils/sns";
import { Button, Icon, Typography } from "@backpac/monica";

export function MyCampaignsPage() {
  return (
    <PageContainer
      nav={
        <PageContainer.Navigation
          title="Campaigns"
          links={[
            { to: routes.CAMPAIGNS.root, text: "Discover" },
            { to: routes.CAMPAIGNS.my, text: "My campaigns" },
          ]}
        />
      }
    >
      <Contents />
    </PageContainer>
  );
}

// TODO: For now, we don't have correct channel Id (tiktok integration is not completed yet), so we cannot display the campaign status correctly. (For example, someone registered Benson Boone campaign, but we cannot display the campaign status as "Ongoing" because we don't have the channel id of registered user)
function Contents() {
  const workspaceId = useWorkspaceId();
  const {
    data: {
      data: { items },
    },
  } = useGetCampaignsApiV1Suspense(workspaceId);

  const campaigns = items
    .filter((campaign) => campaign.userRegisterStatus === CampaignUserRegisterStatus.REGISTERED)
    .filter((campaign) => campaign.id !== DEBUT_BOONE_CAMPAIGN_ID);

  if (campaigns.length === 0) {
    return <Empty />;
  }

  return (
    <div>
      <Typography size="sm" color="textMuted" className="mb-6">
        {campaigns.length} campaigns
      </Typography>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
        {campaigns.map((campaign) => {
          if (campaign.campaignStatus === "CLOSED") {
            return (
              <CampaignCard
                key={campaign.id}
                status={campaign.campaignStatus}
                campaignTitle={campaign.title}
                materialTitle={`${campaign.artistName} - ${campaign.trackName}`}
                thumbnail={CAMPAIGN_INFO[campaign.id]?.material.cover}
                applyDates={calculateRemainingDays(CAMPAIGN_INFO[campaign.id]?.campaignUploadEndDate)}
                platform={getSnsLabel(CAMPAIGN_INFO[campaign.id]?.platform)}
              />
            );
          }

          return (
            <WorkspaceLink key={campaign.id} to={routes.CAMPAIGNS._view(campaign.id)}>
              <CampaignCard
                campaignTitle={campaign.title}
                materialTitle={`${campaign.artistName} - ${campaign.trackName}`}
                applyDates={calculateRemainingDays(CAMPAIGN_INFO[campaign.id]?.campaignUploadEndDate)}
                status={campaign.campaignStatus}
                thumbnail={CAMPAIGN_INFO[campaign.id]?.material.cover}
                platform={getSnsLabel(CAMPAIGN_INFO[campaign.id]?.platform)}
              />
            </WorkspaceLink>
          );
        })}
      </div>
    </div>
  );
}

function Empty() {
  const navigate = useWorkspaceNavigate();

  return (
    <div className="h-[calc(100vh-280px)] flex flex-col items-center justify-center pb-10">
      <Icon name="announce" bgColor="brandTangerine" iconSize="16px" />
      <Typography size="lg" weight={600} className="mb-2 mt-3">
        No campaigns joined yet
      </Typography>
      <Typography size="paragraphXs" color="textMuted" className="mb-4 text-center">
        You haven't joined any campaigns yet.
        <br />
        Discover new opportunities and start earning!
      </Typography>
      <Button onClick={() => navigate(routes.CAMPAIGNS.root)}>Discover campaigns</Button>
    </div>
  );
}
